import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Download, Loader2, PenLine, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { studio } from "@/lib/studioApi";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ZOOMS = [0.6, 0.8, 1, 1.25, 1.5, 2];

/* Prévisualisation PDF intégrée : le document tel que le client le recevra, zoom + pages, sans téléchargement. */
export const PdfPreviewDialog = ({ open, onClose, url, filename, title = "Aperçu du document", editLabel = "Retour à l'édition", testId = "pdf-preview" }) => {
  const [blob, setBlob] = useState(null);
  const [error, setError] = useState("");
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [zoomIdx, setZoomIdx] = useState(2);
  const [width, setWidth] = useState(0);

  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setBlob(null);
    setError("");
    setPage(1);
    studio.get(url, { responseType: "blob", timeout: 120000 })
      .then((res) => active && setBlob(res.data))
      .catch(() => active && setError("Impossible de générer l'aperçu pour le moment."));
    const esc = (e) => e.key === "Escape" && closeRef.current();
    window.addEventListener("keydown", esc);
    document.body.style.overflow = "hidden";
    return () => { active = false; window.removeEventListener("keydown", esc); document.body.style.overflow = ""; };
  }, [open, url]);

  useEffect(() => {
    if (!open) return undefined;
    const measure = () => setWidth(Math.min(window.innerWidth - (window.innerWidth >= 1024 ? 96 : 24), 820));
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open]);

  const [fileData, setFileData] = useState(null);
  useEffect(() => {
    if (!blob) return setFileData(null);
    blob.arrayBuffer().then((buf) => setFileData({ data: new Uint8Array(buf) }));
  }, [blob]);

  const download = () => {
    if (!blob) return;
    const href = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = href;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(href), 30000);
    toast.success("PDF téléchargé.");
  };

  if (!open) return null;
  const zoom = ZOOMS[zoomIdx];

  return (
    <div data-testid={testId} role="dialog" aria-modal="true" aria-label={title} className="fixed inset-0 z-[80] flex flex-col bg-[#0F2645]/[0.92] backdrop-blur-sm" style={{ animation: "devFadeUp .18s ease-out" }}>
      <div className="flex h-14 shrink-0 items-center gap-2 px-3 text-white sm:px-5">
        <div className="min-w-0 flex-1">
          <p className="truncate font-heading text-sm font-bold sm:text-base">{title}</p>
          <p className="hidden text-[11px] text-white/60 sm:block">Rendu identique au document reçu par le client</p>
        </div>
        <div className="hidden items-center gap-0.5 rounded-xl bg-white/10 p-0.5 sm:flex">
          <button type="button" data-testid={`${testId}-zoom-out`} disabled={zoomIdx === 0} onClick={() => setZoomIdx((z) => z - 1)} className="inline-flex h-9 w-9 items-center justify-center rounded-lg hover:bg-white/10 disabled:opacity-30" aria-label="Zoom arrière"><ZoomOut className="h-4 w-4" /></button>
          <span data-testid={`${testId}-zoom`} className="w-12 text-center font-mono text-xs tabular-nums">{Math.round(zoom * 100)} %</span>
          <button type="button" data-testid={`${testId}-zoom-in`} disabled={zoomIdx === ZOOMS.length - 1} onClick={() => setZoomIdx((z) => z + 1)} className="inline-flex h-9 w-9 items-center justify-center rounded-lg hover:bg-white/10 disabled:opacity-30" aria-label="Zoom avant"><ZoomIn className="h-4 w-4" /></button>
        </div>
        {pages > 1 && (
          <div className="flex items-center gap-0.5 rounded-xl bg-white/10 p-0.5">
            <button type="button" data-testid={`${testId}-prev`} disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="inline-flex h-9 w-9 items-center justify-center rounded-lg hover:bg-white/10 disabled:opacity-30" aria-label="Page précédente"><ChevronLeft className="h-4 w-4" /></button>
            <span data-testid={`${testId}-page`} className="px-1 font-mono text-xs tabular-nums">{page} / {pages}</span>
            <button type="button" data-testid={`${testId}-next`} disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="inline-flex h-9 w-9 items-center justify-center rounded-lg hover:bg-white/10 disabled:opacity-30" aria-label="Page suivante"><ChevronRight className="h-4 w-4" /></button>
          </div>
        )}
        <button type="button" data-testid={`${testId}-download`} disabled={!blob} onClick={download} className="inline-flex h-10 items-center gap-2 rounded-xl bg-white/10 px-3 text-sm font-semibold hover:bg-white/20 disabled:opacity-40">
          <Download className="h-4 w-4" /> <span className="hidden sm:inline">Télécharger PDF</span>
        </button>
        <button type="button" data-testid={`${testId}-close`} onClick={onClose} className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20" aria-label="Fermer"><X className="h-5 w-5" /></button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto px-3 pb-24 pt-2 sm:px-6" data-testid={`${testId}-viewport`}>
        {error && (
          <div data-testid={`${testId}-error`} className="mx-auto mt-16 max-w-sm rounded-2xl bg-white p-6 text-center">
            <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" />
            <p className="mt-3 text-sm font-semibold text-[#0F2645]">{error}</p>
            <button type="button" onClick={onClose} className="mt-4 inline-flex min-h-[42px] items-center rounded-xl bg-[#3A5CC8] px-4 text-sm font-semibold text-white">Fermer</button>
          </div>
        )}
        {!error && !fileData && (
          <div data-testid={`${testId}-loading`} className="flex flex-col items-center justify-center gap-3 pt-24 text-white/80">
            <Loader2 className="h-7 w-7 animate-spin" />
            <p className="text-sm">Génération de l'aperçu…</p>
          </div>
        )}
        {fileData && (
          <Document
            file={fileData}
            onLoadSuccess={({ numPages }) => setPages(numPages)}
            onLoadError={() => setError("Le PDF n'a pas pu être affiché.")}
            loading={<div className="flex justify-center pt-24 text-white/80"><Loader2 className="h-7 w-7 animate-spin" /></div>}
            className="flex justify-center"
          >
            <div className="mx-auto w-fit rounded-md bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]" style={{ animation: "devFadeUp .25s ease-out" }}>
              <Page pageNumber={page} width={width * zoom} renderTextLayer={false} renderAnnotationLayer={false} />
            </div>
          </Document>
        )}
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center pb-[max(16px,env(safe-area-inset-bottom))] pt-6" style={{ background: "linear-gradient(to top, rgba(15,38,69,0.9), rgba(15,38,69,0))" }}>
        <div className="pointer-events-auto flex items-center gap-2 rounded-2xl bg-white p-1.5 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.5)]">
          <div className="flex items-center sm:hidden">
            <button type="button" disabled={zoomIdx === 0} onClick={() => setZoomIdx((z) => z - 1)} className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-[#0F2645] hover:bg-[#F3F5FC] disabled:opacity-30" aria-label="Zoom arrière"><ZoomOut className="h-4 w-4" /></button>
            <button type="button" disabled={zoomIdx === ZOOMS.length - 1} onClick={() => setZoomIdx((z) => z + 1)} className="inline-flex h-10 w-10 items-center justify-center rounded-xl text-[#0F2645] hover:bg-[#F3F5FC] disabled:opacity-30" aria-label="Zoom avant"><ZoomIn className="h-4 w-4" /></button>
          </div>
          <button type="button" data-testid={`${testId}-edit`} onClick={onClose} className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#0F2645] px-4 text-sm font-semibold text-white hover:bg-[#1B3561]">
            <PenLine className="h-4 w-4" /> {editLabel}
          </button>
        </div>
      </div>
    </div>
  );
};
